import { useState, useEffect, useRef, useCallback } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { AreaChart, Area, ResponsiveContainer, YAxis, Tooltip } from "recharts";
import { Play, Pause, Activity, ShieldCheck, Gamepad2, Globe2, Zap } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { id: "overview", label: "Overview", icon: Activity },
  { id: "threats", label: "Threats", icon: ShieldCheck },
  { id: "cheat", label: "Anti-Cheat", icon: Gamepad2 },
];

const ACTION_COLOR = {
  BLOCKED: "#F43F5E",
  QUARANTINED: "#FACC15",
  MITIGATED: "#22D3EE",
  CHALLENGED: "#94A3B8",
};

function ChartTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#05050A] border border-[#22D3EE]/40 px-3 py-1.5 text-xs font-mono text-[#22D3EE]">
      {payload[0].value} req/s
    </div>
  );
}

export default function SecurityDashboard() {
  const [tab, setTab] = useState("overview");
  const [events, setEvents] = useState([]);
  const [series, setSeries] = useState([]);
  const [blockedPct, setBlockedPct] = useState(99.98);
  const [live, setLive] = useState(true);
  const [region, setRegion] = useState("all");
  const timer = useRef(null);

  const pull = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/threat-feed?limit=6`);
      setEvents((prev) => [...data.events, ...prev].slice(0, 40));
      setSeries(data.series.map((v, i) => ({ i, v })));
      setBlockedPct(data.blocked_pct);
    } catch (_) {}
  }, []);

  useEffect(() => { pull(); }, [pull]);

  useEffect(() => {
    if (live) {
      timer.current = setInterval(() => {
        axios.get(`${API}/threat-feed?limit=2`).then(({ data }) => {
          setEvents((prev) => [...data.events, ...prev].slice(0, 40));
          setSeries(data.series.map((v, i) => ({ i, v })));
          setBlockedPct(data.blocked_pct);
        }).catch(() => {});
      }, 2200);
    }
    return () => clearInterval(timer.current);
  }, [live]);

  const regions = ["all", ...Array.from(new Set(events.map((e) => e.region)))].slice(0, 8);
  const filtered = events
    .filter((e) => (region === "all" ? true : e.region === region))
    .filter((e) => (tab === "cheat" ? e.kind === "cheat" : true));

  const cheatCount = events.filter((e) => e.kind === "cheat").length;

  return (
    <section id="dashboard" className="relative py-28 lg:py-40 bg-[#05050A] overflow-hidden" data-testid="dashboard-section">
      <div className="absolute inset-0 bg-grid opacity-30" />
      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <span className="text-xs uppercase tracking-[0.3em] text-[#22D3EE]">Live preview</span>
            <h2 className="mt-5 font-display font-900 text-4xl sm:text-5xl lg:text-6xl tracking-tighter text-white leading-none">
              The Security<br />Dashboard
            </h2>
          </div>
          <p className="max-w-sm text-sm text-slate-400 leading-relaxed">
            A real-time window into the sphere. Watch threats get intercepted,
            requests routed and cheats flagged across the global edge.
          </p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="rounded-2xl border border-white/10 bg-[#0A0A12]/90 backdrop-blur-xl overflow-hidden"
          data-testid="security-dashboard"
        >
          {/* Top bar */}
          <div className="flex items-center justify-between px-5 py-3.5 border-b border-white/10 bg-[#07070E]">
            <div className="flex items-center gap-2">
              <span className="flex gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full bg-[#F43F5E]/70" />
                <span className="w-2.5 h-2.5 rounded-full bg-[#FACC15]/70" />
                <span className="w-2.5 h-2.5 rounded-full bg-[#22D3EE]/70" />
              </span>
              <span className="ml-3 font-mono text-xs text-slate-500">endasphere://control-plane</span>
            </div>
            <button
              onClick={() => setLive((v) => !v)}
              data-testid="dashboard-live-toggle"
              className={`inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-[11px] uppercase tracking-[0.15em] font-700 transition-colors duration-300 ${
                live ? "bg-[#22D3EE]/15 text-[#22D3EE]" : "bg-white/5 text-slate-400"
              }`}
            >
              {live ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />}
              {live ? "Live" : "Paused"}
            </button>
          </div>

          {/* Tabs */}
          <div className="flex items-center gap-1 px-3 pt-3 border-b border-white/10 overflow-x-auto scrollbar-hide">
            {TABS.map((t) => {
              const Icon = t.icon;
              return (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  data-testid={`dashboard-tab-${t.id}`}
                  className={`relative inline-flex items-center gap-2 px-4 py-2.5 text-xs uppercase tracking-[0.15em] whitespace-nowrap transition-colors duration-300 ${
                    tab === t.id ? "text-white" : "text-slate-500 hover:text-slate-300"
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {t.label}
                  {tab === t.id && (
                    <motion.span layoutId="tab-underline" className="absolute left-2 right-2 -bottom-px h-0.5 bg-[#22D3EE]" />
                  )}
                </button>
              );
            })}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-white/5">
            {/* Left: metrics + chart */}
            <div className="lg:col-span-2 bg-[#0A0A12] p-6 lg:p-8">
              <div className="grid grid-cols-3 gap-4">
                {[
                  { icon: ShieldCheck, label: "Blocked", value: `${blockedPct}%`, c: "#22D3EE" },
                  { icon: tab === "cheat" ? Gamepad2 : Globe2, label: tab === "cheat" ? "Cheats/min" : "Edge nodes", value: tab === "cheat" ? cheatCount * 4 : "312", c: "#FACC15" },
                  { icon: Zap, label: "Avg latency", value: "11ms", c: "#F43F5E" },
                ].map((m) => {
                  const Icon = m.icon;
                  return (
                    <div key={m.label} className="rounded-xl border border-white/10 p-4" data-testid={`dashboard-metric-${m.label.toLowerCase().replace(/[^a-z]/g, "-")}`}>
                      <Icon className="w-4 h-4" style={{ color: m.c }} />
                      <div className="mt-3 font-display font-700 text-xl lg:text-2xl text-white tabular-nums">{m.value}</div>
                      <div className="mt-1 text-[10px] uppercase tracking-[0.15em] text-slate-500">{m.label}</div>
                    </div>
                  );
                })}
              </div>

              <div className="mt-6">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-[11px] uppercase tracking-[0.2em] text-slate-500">Requests / sec · last 24 windows</span>
                  <span className="font-mono text-xs text-[#22D3EE]">{live ? "● streaming" : "○ paused"}</span>
                </div>
                <div className="h-40 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={series} margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
                      <defs>
                        <linearGradient id="cyanFill" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#22D3EE" stopOpacity={0.5} />
                          <stop offset="100%" stopColor="#22D3EE" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <YAxis hide domain={[0, 1000]} />
                      <Tooltip content={<ChartTooltip />} cursor={{ stroke: "#22D3EE", strokeOpacity: 0.2 }} />
                      <Area type="monotone" dataKey="v" stroke="#22D3EE" strokeWidth={2} fill="url(#cyanFill)" isAnimationActive={false} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Region filter */}
              <div className="mt-6 flex flex-wrap gap-2">
                {regions.map((r) => (
                  <button
                    key={r}
                    onClick={() => setRegion(r)}
                    data-testid={`dashboard-region-${r}`}
                    className={`rounded-full border px-3 py-1.5 text-[10px] font-mono uppercase tracking-[0.1em] transition-colors duration-300 ${
                      region === r ? "border-[#22D3EE] text-[#22D3EE] bg-[#22D3EE]/10" : "border-white/10 text-slate-500 hover:text-slate-300"
                    }`}
                  >
                    {r}
                  </button>
                ))}
              </div>
            </div>

            {/* Right: live feed */}
            <div className="bg-[#07070E] p-6 lg:p-8 flex flex-col">
              <div className="flex items-center justify-between mb-4">
                <span className="text-[11px] uppercase tracking-[0.2em] text-slate-500">
                  {tab === "cheat" ? "Cheat detections" : "Threat feed"}
                </span>
                <span className="font-mono text-[10px] text-slate-600">{filtered.length} events</span>
              </div>
              <div className="flex-1 space-y-2 max-h-[360px] overflow-y-auto scrollbar-hide" data-testid="dashboard-feed">
                {filtered.length === 0 && (
                  <p className="font-mono text-xs text-slate-600 py-6">Awaiting events…</p>
                )}
                {filtered.map((e) => (
                  <motion.div
                    key={e.id}
                    initial={{ opacity: 0, x: 16 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4 }}
                    className="group flex items-center gap-3 rounded-lg border border-white/5 bg-[#0A0A12] px-3 py-2.5 hover:border-white/15 transition-colors duration-300"
                  >
                    <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: e.color }} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-mono text-xs text-white truncate">{e.label}</span>
                        <span className="font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded" style={{ color: ACTION_COLOR[e.action], backgroundColor: `${ACTION_COLOR[e.action]}1a` }}>
                          {e.action}
                        </span>
                      </div>
                      <div className="mt-0.5 flex items-center gap-2 font-mono text-[10px] text-slate-600">
                        <span>{e.ip}</span>
                        <span className="text-slate-700">·</span>
                        <span>{e.region}</span>
                        <span className="text-slate-700">·</span>
                        <span className="text-[#22D3EE]/70">{e.latency_ms}ms</span>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
